/** Shared period filter helpers for Leads / Cotizaciones summary tables. */

export const PERIOD_OPTIONS = [
  { value: 'last_12', label: 'Últimos 12 meses' },
  { value: 'last_36', label: 'Últimos 36 meses' },
  { value: 'all', label: 'Todos los períodos' },
  { value: 'monthly', label: 'Mensual' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'annual', label: 'Anual' },
  { value: 'annual_ytd', label: 'Anual YTD' },
  { value: 'current_month', label: 'Mes actual' },
  { value: 'previous_month', label: 'Mes anterior' },
  { value: 'last_3', label: 'Últimos 3 meses' },
  { value: 'last_6', label: 'Últimos 6 meses' },
  { value: 'ytd', label: 'Año en curso' },
];

export const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
export const MONTH_OPTIONS = MONTH_LABELS.map((label, index) => ({ value: String(index), label }));
export const QUARTER_LABELS = ['T1', 'T2', 'T3', 'T4'];
export const QUARTER_OPTIONS = QUARTER_LABELS.map((label, index) => ({
  value: String(index),
  label: `${label} (${MONTH_LABELS[index * 3]}–${MONTH_LABELS[index * 3 + 2]})`,
}));
export const SEMESTER_LABELS = ['S1', 'S2'];
export const SEMESTER_OPTIONS = SEMESTER_LABELS.map((label, index) => ({
  value: String(index),
  label: `${label} (${MONTH_LABELS[index * 6]}–${MONTH_LABELS[index * 6 + 5]})`,
}));

export const parseDateSafe = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  // Fecha calendario YYYY-MM-DD → local (sin interpretar como UTC midnight).
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    const date = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
export const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
export const addMonths = (date, amount) => new Date(date.getFullYear(), date.getMonth() + amount, 1);
export const startOfYear = (year) => new Date(year, 0, 1);
export const endOfYear = (year) => new Date(year, 11, 31, 23, 59, 59, 999);

export const getMonthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
export const getMonthLabel = (date) => `${MONTH_LABELS[date.getMonth()]} ${date.getFullYear()}`;

/** Quincena: 1–15 (Q1) o 16–fin de mes (Q2). */
export const getQuincenaHalf = (date) => (date.getDate() <= 15 ? 1 : 2);

export const getQuincenaKey = (date) => `${getMonthKey(date)}-Q${getQuincenaHalf(date)}`;

export const getQuincenaLabel = (date) => {
  const month = MONTH_LABELS[date.getMonth()];
  const year = date.getFullYear();
  if (getQuincenaHalf(date) === 1) return `1–15 ${month} ${year}`;
  const lastDay = endOfMonth(date).getDate();
  return `16–${lastDay} ${month} ${year}`;
};

export const getQuincenaBounds = (date) => {
  const y = date.getFullYear();
  const m = date.getMonth();
  if (getQuincenaHalf(date) === 1) {
    return {
      key: `${getMonthKey(date)}-Q1`,
      label: getQuincenaLabel(new Date(y, m, 1)),
      start: new Date(y, m, 1),
      end: new Date(y, m, 15, 23, 59, 59, 999),
    };
  }
  return {
    key: `${getMonthKey(date)}-Q2`,
    label: getQuincenaLabel(new Date(y, m, 16)),
    start: new Date(y, m, 16),
    end: endOfMonth(date),
  };
};

export const getQuincenasInRange = (start, end) => {
  if (!start || !end) return [];
  const rangeStart = parseDateSafe(start) || start;
  const rangeEnd = parseDateSafe(end) || end;
  if (!(rangeStart instanceof Date) || !(rangeEnd instanceof Date)) return [];
  if (Number.isNaN(rangeStart.getTime()) || Number.isNaN(rangeEnd.getTime())) return [];

  const quincenas = [];
  let cursor = startOfMonth(rangeStart);
  const lastMonth = startOfMonth(rangeEnd);
  while (cursor <= lastMonth) {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const halves = [
      {
        key: `${getMonthKey(cursor)}-Q1`,
        label: getQuincenaLabel(new Date(y, m, 1)),
        start: new Date(y, m, 1),
        end: new Date(y, m, 15, 23, 59, 59, 999),
      },
      {
        key: `${getMonthKey(cursor)}-Q2`,
        label: getQuincenaLabel(new Date(y, m, 16)),
        start: new Date(y, m, 16),
        end: endOfMonth(cursor),
      },
    ];
    halves.forEach((bucket) => {
      if (bucket.end >= rangeStart && bucket.start <= rangeEnd) {
        quincenas.push(bucket);
      }
    });
    cursor = addMonths(cursor, 1);
  }
  return quincenas;
};

export const collectDates = (records, getDateValue) =>
  (Array.isArray(records) ? records : [])
    .map((row) => parseDateSafe(typeof getDateValue === 'function' ? getDateValue(row) : row?.[getDateValue]))
    .filter(Boolean);

export const getAvailableYearsFromDates = (dates) => {
  const years = new Set((dates || []).map((date) => date.getFullYear()));
  years.add(new Date().getFullYear());
  return Array.from(years).sort((a, b) => a - b);
};

export const getMinDate = (dates) => {
  const sorted = [...(dates || [])].sort((a, b) => a - b);
  return sorted[0] || new Date();
};

export const getMonthsInRange = (start, end) => {
  const months = [];
  let cursor = startOfMonth(start);
  const endCursor = startOfMonth(end);
  while (cursor <= endCursor) {
    months.push({
      key: getMonthKey(cursor),
      label: getMonthLabel(cursor),
      start: startOfMonth(cursor),
      end: endOfMonth(cursor),
    });
    cursor = addMonths(cursor, 1);
  }
  return months;
};

export const getYearsAsPeriods = (years) =>
  years.map((year) => ({
    key: `year-${year}`,
    label: String(year),
    start: startOfYear(year),
    end: endOfYear(year),
  }));

/** Columnas anuales acotadas a ene → mes actual (mismo mes en cada año). */
export const getYearsAsYtdPeriods = (years, asOfDate = new Date()) => {
  const monthIndex = asOfDate.getMonth();
  const monthLabel = MONTH_LABELS[monthIndex];
  return years.map((year) => {
    const end = endOfMonth(new Date(year, monthIndex, 1));
    return {
      key: `year-ytd-${year}`,
      label: String(year),
      shortLabel: `${year} · Ene-${monthLabel}`,
      start: startOfYear(year),
      end,
    };
  });
};

/** Trimestres calendario (T1 ene–mar … T4 oct–dic) para uno o más años. */
export const getQuartersAsPeriods = (years, asOfDate = new Date()) => {
  const columns = [];
  const asOf = startOfMonth(asOfDate);
  (years || []).forEach((year) => {
    for (let quarter = 0; quarter < 4; quarter += 1) {
      const startMonth = quarter * 3;
      const start = startOfMonth(new Date(year, startMonth, 1));
      if (start > asOf) continue;
      const end = endOfMonth(new Date(year, startMonth + 2, 1));
      columns.push({
        key: `q-${year}-${quarter + 1}`,
        label: `${QUARTER_LABELS[quarter]} ${year}`,
        shortLabel: `${QUARTER_LABELS[quarter]} '${String(year).slice(-2)}`,
        start,
        end,
      });
    }
  });
  return columns;
};

/** Semestres calendario (S1 ene–jun, S2 jul–dic) para uno o más años. */
export const getSemestersAsPeriods = (years, asOfDate = new Date()) => {
  const columns = [];
  const asOf = startOfMonth(asOfDate);
  (years || []).forEach((year) => {
    for (let semester = 0; semester < 2; semester += 1) {
      const startMonth = semester * 6;
      const start = startOfMonth(new Date(year, startMonth, 1));
      if (start > asOf) continue;
      const end = endOfMonth(new Date(year, startMonth + 5, 1));
      columns.push({
        key: `s-${year}-${semester + 1}`,
        label: `${SEMESTER_LABELS[semester]} ${year}`,
        shortLabel: `${SEMESTER_LABELS[semester]} '${String(year).slice(-2)}`,
        start,
        end,
      });
    }
  });
  return columns;
};

const clipBucketToRange = (rawStart, rawEnd, rangeStart, rangeEnd, key, label, shortLabel) => {
  const start = rawStart < rangeStart ? rangeStart : rawStart;
  const end = rawEnd > rangeEnd ? rangeEnd : rawEnd;
  if (end < start) return null;
  return { key, label, shortLabel, start, end };
};

/** Trimestres calendario recortados al rango (para ejes de gráficos). */
export const getQuartersInRange = (start, end) => {
  if (!start || !end) return [];
  const rangeStart = startOfMonth(start);
  const rangeEnd = end;
  const columns = [];
  const startMonth = Math.floor(rangeStart.getMonth() / 3) * 3;
  let cursor = new Date(rangeStart.getFullYear(), startMonth, 1);
  while (cursor <= rangeEnd) {
    const year = cursor.getFullYear();
    const quarter = Math.floor(cursor.getMonth() / 3);
    const bucket = clipBucketToRange(
      startOfMonth(new Date(year, quarter * 3, 1)),
      endOfMonth(new Date(year, quarter * 3 + 2, 1)),
      rangeStart,
      rangeEnd,
      `q-${year}-${quarter + 1}`,
      `${QUARTER_LABELS[quarter]} ${year}`,
      `${QUARTER_LABELS[quarter]} '${String(year).slice(-2)}`
    );
    if (bucket) columns.push(bucket);
    cursor = addMonths(cursor, 3);
  }
  return columns;
};

/** Semestres calendario recortados al rango (para ejes de gráficos). */
export const getSemestersInRange = (start, end) => {
  if (!start || !end) return [];
  const rangeStart = startOfMonth(start);
  const rangeEnd = end;
  const columns = [];
  const startMonth = Math.floor(rangeStart.getMonth() / 6) * 6;
  let cursor = new Date(rangeStart.getFullYear(), startMonth, 1);
  while (cursor <= rangeEnd) {
    const year = cursor.getFullYear();
    const semester = Math.floor(cursor.getMonth() / 6);
    const bucket = clipBucketToRange(
      startOfMonth(new Date(year, semester * 6, 1)),
      endOfMonth(new Date(year, semester * 6 + 5, 1)),
      rangeStart,
      rangeEnd,
      `s-${year}-${semester + 1}`,
      `${SEMESTER_LABELS[semester]} ${year}`,
      `${SEMESTER_LABELS[semester]} '${String(year).slice(-2)}`
    );
    if (bucket) columns.push(bucket);
    cursor = addMonths(cursor, 6);
  }
  return columns;
};

/** Años calendario recortados al rango (para ejes de gráficos). */
export const getYearsInRange = (start, end) => {
  if (!start || !end) return [];
  const rangeStart = startOfMonth(start);
  const rangeEnd = end;
  const columns = [];
  for (let year = rangeStart.getFullYear(); year <= rangeEnd.getFullYear(); year += 1) {
    const bucket = clipBucketToRange(
      startOfYear(year),
      endOfYear(year),
      rangeStart,
      rangeEnd,
      `year-${year}`,
      String(year),
      String(year)
    );
    if (bucket) columns.push(bucket);
  }
  return columns;
};

/**
 * Eje X de gráficos: si el período es largo, agrupa meses en trimestres,
 * semestres o años para que las etiquetas quepan.
 */
export const resolveChartAxisColumns = (selectedPeriod) => {
  if (!selectedPeriod) return [];
  if (selectedPeriod.mode && selectedPeriod.mode !== 'month') {
    return selectedPeriod.columns || [];
  }
  const columns = selectedPeriod.columns || [];
  const range = selectedPeriod.range;
  const count = columns.length;
  if (!range?.start || !range?.end || count <= 18) return columns;
  if (count <= 48) return getQuartersInRange(range.start, range.end);
  const semesters = getSemestersInRange(range.start, range.end);
  if (semesters.length <= 20) return semesters;
  return getYearsInRange(range.start, range.end);
};

export const resolvePeriodSelection = ({
  period,
  dateValues = [],
  secondaryYear,
  secondaryMonth,
  titles = {},
}) => {
  const now = new Date();
  const currentMonthStart = startOfMonth(now);
  const availableYears = getAvailableYearsFromDates(dateValues);
  const fallbackYear = availableYears.includes(now.getFullYear())
    ? now.getFullYear()
    : availableYears[availableYears.length - 1] || now.getFullYear();
  const year = Number(secondaryYear) || fallbackYear;
  const monthIndex = Number.isFinite(Number(secondaryMonth)) ? Number(secondaryMonth) : now.getMonth();

  const monthlyTitle = titles.monthly || 'Resumen comercial mensual';
  const annualTitle = titles.annual || 'Resumen comercial anual';
  const annualYtdTitle = titles.annualYtd || `${annualTitle} YTD (ene–${MONTH_LABELS[now.getMonth()]})`;
  const quarterlyTitle = titles.quarterly || 'Resumen comercial trimestral';
  const semestralTitle = titles.semestral || 'Resumen comercial semestral';

  if (period === 'annual') {
    const years =
      secondaryYear === 'all'
        ? availableYears
        : availableYears.includes(year)
          ? [year]
          : [fallbackYear];
    const columns = getYearsAsPeriods(years);
    return {
      mode: 'year',
      range: {
        start: columns[0]?.start || startOfYear(fallbackYear),
        end: columns[columns.length - 1]?.end || endOfYear(fallbackYear),
      },
      columns,
      title: annualTitle,
    };
  }

  if (period === 'annual_ytd') {
    const years =
      secondaryYear === 'all'
        ? availableYears
        : availableYears.includes(year)
          ? [year]
          : [fallbackYear];
    const columns = getYearsAsYtdPeriods(years, now);
    return {
      mode: 'year_ytd',
      range: {
        start: columns[0]?.start || startOfYear(fallbackYear),
        end: columns[columns.length - 1]?.end || endOfMonth(now),
      },
      columns,
      title: annualYtdTitle,
    };
  }

  if (period === 'quarterly') {
    const years =
      secondaryYear === 'all'
        ? availableYears
        : availableYears.includes(year)
          ? [year]
          : [fallbackYear];
    const columns = getQuartersAsPeriods(years, now);
    return {
      mode: 'quarter',
      range: {
        start: columns[0]?.start || startOfYear(fallbackYear),
        end: columns[columns.length - 1]?.end || endOfMonth(now),
      },
      columns,
      title: quarterlyTitle,
    };
  }

  if (period === 'semestral') {
    const years =
      secondaryYear === 'all'
        ? availableYears
        : availableYears.includes(year)
          ? [year]
          : [fallbackYear];
    const columns = getSemestersAsPeriods(years, now);
    return {
      mode: 'semester',
      range: {
        start: columns[0]?.start || startOfYear(fallbackYear),
        end: columns[columns.length - 1]?.end || endOfMonth(now),
      },
      columns,
      title: semestralTitle,
    };
  }

  if (period === 'monthly') {
    const monthStart = new Date(year, monthIndex, 1);
    const columns = getMonthsInRange(startOfYear(year), endOfYear(year));
    return {
      mode: 'month',
      range: { start: startOfMonth(monthStart), end: endOfMonth(monthStart) },
      columns,
      title: monthlyTitle,
    };
  }

  let range;
  if (period === 'all') {
    range = {
      start: startOfMonth(getMinDate(dateValues)),
      end: endOfMonth(now),
    };
  } else if (period === 'current_month') {
    range = { start: currentMonthStart, end: endOfMonth(now) };
  } else if (period === 'previous_month') {
    const start = addMonths(currentMonthStart, -1);
    range = { start, end: endOfMonth(start) };
  } else if (period === 'last_3') {
    range = { start: addMonths(currentMonthStart, -2), end: endOfMonth(now) };
  } else if (period === 'last_6') {
    range = { start: addMonths(currentMonthStart, -5), end: endOfMonth(now) };
  } else if (period === 'last_24') {
    range = { start: addMonths(currentMonthStart, -23), end: endOfMonth(now) };
  } else if (period === 'last_36') {
    range = { start: addMonths(currentMonthStart, -35), end: endOfMonth(now) };
  } else if (period === 'ytd') {
    range = { start: startOfYear(now.getFullYear()), end: endOfMonth(now) };
  } else {
    range = { start: addMonths(currentMonthStart, -11), end: endOfMonth(now) };
  }

  return {
    mode: 'month',
    range,
    columns: getMonthsInRange(range.start, range.end),
    title: monthlyTitle,
  };
};

export const isDateInRange = (value, range) => {
  const date = parseDateSafe(value);
  if (!date || !range) return false;
  return date >= range.start && date <= range.end;
};

/** Incluye cierres programados del mes siguiente en filtros rodantes de ventas. */
export const withSalesPeriodFutureCloseBuffer = (selectedPeriod, periodFilter) => {
  if (!selectedPeriod?.range || periodFilter === 'all') return selectedPeriod;
  const bufferedFilters = new Set(['last_12', 'last_36', 'last_3', 'last_6']);
  if (!bufferedFilters.has(periodFilter)) return selectedPeriod;

  const now = new Date();
  const bufferEnd = endOfMonth(addMonths(startOfMonth(now), 1));
  if (selectedPeriod.range.end >= bufferEnd) return selectedPeriod;

  const range = { ...selectedPeriod.range, end: bufferEnd };
  return {
    ...selectedPeriod,
    range,
    columns:
      selectedPeriod.mode === 'month'
        ? getMonthsInRange(range.start, range.end)
        : selectedPeriod.columns,
  };
};

/** Filtra por rango global, o por pertenencia a alguna columna (Anual YTD). */
export const isDateInSelectedPeriod = (value, selectedPeriod) => {
  if (!selectedPeriod) return true;
  if (selectedPeriod.mode === 'year_ytd') {
    const date = parseDateSafe(value);
    if (!date) return false;
    const columns = selectedPeriod.columns || [];
    return columns.some((bucket) => date >= bucket.start && date <= bucket.end);
  }
  return isDateInRange(value, selectedPeriod.range);
};

export const periodSelectStyle = (ui) => ({
  height: '24px',
  borderRadius: '6px',
  border: `1px solid ${ui.inputBorder}`,
  background: ui.inputBg,
  color: ui.inputText || ui.text,
  padding: '0 8px',
  fontSize: '11px',
  fontWeight: 600,
  outline: 'none',
  cursor: 'pointer',
  minWidth: '120px',
  lineHeight: '24px',
});

export const periodLabelStyle = (ui) => ({
  fontSize: '9px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: ui.muted,
  lineHeight: 1.1,
});
